import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useCart } from "../context/CartContextCliente";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import { db } from "../firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";

// ✅ Calendario
import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";

import {
  addDays,
  differenceInCalendarDays,
  isSameDay,
  isBefore,
  startOfDay,
} from "date-fns";

const ordenTalles = [
  "0","1","2","4","6","8","10","12","14","16","18",
  "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "4XL",
  ...Array.from({ length: 35 }, (_, i) => (20 + i).toString())
];

const fmtAR = (d) => {
  if (!d) return "-";
  return d.toLocaleDateString("es-AR");
};

// ✅ Helper: clave única por producto (y talle si aplica)
const itemKey = (id, talle) => `${id}__${(talle || "").trim().toUpperCase()}`;

const getOrdenTalle = (value) => {
  const idx = ordenTalles.indexOf(String(value || "").trim().toUpperCase());
  return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
};

const parseUnidadNumerica = (value) => {
  const text = String(value || "").trim().toLowerCase().replace(",", ".");
  const match = text.match(/(\d+(?:\.\d+)?)\s*(ml|cc|cl|l|lt|lts|kg|g)\b/);
  if (!match) return null;

  const amount = Number(match[1]);
  const unit = match[2];
  if (!Number.isFinite(amount)) return null;

  const factorMap = {
    ml: 1,
    cc: 1,
    cl: 10,
    l: 1000,
    lt: 1000,
    lts: 1000,
    g: 1,
    kg: 1000,
  };

  return amount * (factorMap[unit] || 1);
};

export default function ProductoCard({ producto, empresa }) {
  const { cart, incrementItem, decrementItem } = useCart();

  const [talles, setTalles] = useState([]);
  const [talleSeleccionado, setTalleSeleccionado] = useState("");
  const [showModal, setShowModal] = useState(false);

  // ✅ Alquiler temporario
  const esAlquiler = empresa?.tipoNegocio === "alquiler_temporario";

  // ✅ Bloqueos y calendario
  const [loadingBloqueos, setLoadingBloqueos] = useState(false);
  const [bloqueos, setBloqueos] = useState([]); 
  const [disabledDates, setDisabledDates] = useState([]);

  // ✅ Reserva
  const minH = Number(producto?.minHuespedes ?? 1);
  const maxH = Number(producto?.maxHuespedes ?? 1);
  const [huespedes, setHuespedes] = useState(minH);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const precioBase = Number(producto?.precioBase ?? producto?.precio ?? 0);
  const precioFallback = Number(producto?.precio ?? 0);
  const imagenFallback = empresa?.logoUrl || "";

  // ✅ Galería modal
  const galeria = useMemo(() => {
    const principal = producto?.imagenUrl ? [producto.imagenUrl] : [];
    const extras = Array.isArray(producto?.imagenesUrls) ? producto.imagenesUrls : [];
    const fallback = principal.length || extras.length ? [] : imagenFallback ? [imagenFallback] : [];
    return Array.from(new Set([...principal, ...extras, ...fallback])).filter(Boolean);
  }, [producto, imagenFallback]);

  const [imgActiva, setImgActiva] = useState(galeria[0] || "");
  useEffect(() => setImgActiva(galeria[0] || ""), [galeria]);

  // ✅ Talles (si aplica)
  useEffect(() => {
    const fetchTalles = async () => {
      if (producto?.talle) {
        try {
          const qTalles = query(
            collection(db, "tallexproducto"),
            where("productoId", "==", producto.id)
          );
          const querySnapshot = await getDocs(qTalles);
          const data = querySnapshot.docs
            .map((doc) => {
              const row = doc.data() || {};
              const talle = String(row.talle || "").trim();
              const precioRow = Number(
                row.precioTalle ?? row.precioMedida ?? row.precio ?? Number.NaN
              );

              return {
                talle,
                precio: Number.isFinite(precioRow) ? precioRow : null,
              };
            })
            .filter((row) => row.talle);

          const ordenados = [...data].sort((a, b) => {
            const precioA = Number.isFinite(a.precio) ? Number(a.precio) : null;
            const precioB = Number.isFinite(b.precio) ? Number(b.precio) : null;

            if (precioA !== null && precioB !== null && precioA !== precioB) {
              return precioB - precioA;
            }

            if (precioA !== null && precioB === null) return -1;
            if (precioA === null && precioB !== null) return 1;

            const unidadA = parseUnidadNumerica(a.talle);
            const unidadB = parseUnidadNumerica(b.talle);
            if (unidadA !== null && unidadB !== null && unidadA !== unidadB) {
              return unidadB - unidadA;
            }

            return getOrdenTalle(a.talle) - getOrdenTalle(b.talle) || a.talle.localeCompare(b.talle, "es", { numeric: true });
          });
          setTalles(ordenados);
        } catch (error) {
          console.error("Error cargando talles:", error);
        }
      } else {
        setTalles([]);
      }
    };
    fetchTalles();
  }, [producto]);

  const tallesConPrecio = useMemo(
    () => talles.filter((row) => Number.isFinite(row?.precio)),
    [talles]
  );

  const precioMinimoTalle = useMemo(() => {
    if (!tallesConPrecio.length) return null;
    return Math.min(...tallesConPrecio.map((row) => Number(row.precio)));
  }, [tallesConPrecio]);

  const preciosPorTalleIguales = useMemo(() => {
    if (!tallesConPrecio.length) return false;
    return new Set(tallesConPrecio.map((row) => Number(row.precio))).size === 1;
  }, [tallesConPrecio]);

  const talleActual = useMemo(() => {
    if (!producto?.talle || !talleSeleccionado) return null;
    return (
      talles.find(
      (row) => String(row?.talle || "").trim().toUpperCase() === talleSeleccionado.trim().toUpperCase()
      ) || null
    );
  }, [producto?.talle, talleSeleccionado, talles]);

  const precioSeleccionado = useMemo(() => {
    return Number.isFinite(talleActual?.precio) ? Number(talleActual.precio) : null;
  }, [talleActual]);

  const tallePrecioMinimo = useMemo(() => {
    if (precioMinimoTalle === null) return null;
    return (
      tallesConPrecio.find((row) => Number(row.precio) === precioMinimoTalle) || null
    );
  }, [precioMinimoTalle, tallesConPrecio]);

  const precioMostrado = precioSeleccionado ?? precioMinimoTalle ?? precioFallback;

  const usaFormatoTalle = useMemo(() => {
    if (!talles.length) return false;
    return talles.every((row) => getOrdenTalle(row.talle) !== Number.MAX_SAFE_INTEGER);
  }, [talles]);

  const textoReferenciaPrecio = useMemo(() => {
    if (esAlquiler) return "x noche";

    if (!producto?.talle) return "cada uno";

    const referencia = talleActual?.talle || (!preciosPorTalleIguales ? tallePrecioMinimo?.talle : "");
    if (!referencia) return "";
    return usaFormatoTalle ? `Medida ${referencia}` : `x ${referencia}`;
  }, [
    esAlquiler,
    producto?.talle,
    talleActual,
    tallePrecioMinimo,
    preciosPorTalleIguales,
    usaFormatoTalle,
  ]);

  // ✅ Cantidad REAL en carrito (por id+talle)
  const qtyEnCarrito = useMemo(() => {
    const id = producto?.id;
    if (!id) return 0;

    const talleKey = producto?.talle ? (talleSeleccionado || "") : "";
    const k = itemKey(id, talleKey);

    const found = (cart || []).find((it) => itemKey(it.id, it.talle) === k);
    return Number(found?.cantidad || 0);
  }, [cart, producto?.id, producto?.talle, talleSeleccionado]);

  // ✅ Helpers WA
  const normalizePhoneForWA = useCallback((raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("549")) return digits;
    if (digits.startsWith("54")) return `549${digits.slice(2)}`;
    const noZero = digits.startsWith("0") ? digits.slice(1) : digits;
    return `549${noZero}`;
  }, []);

  const openWA = useCallback(
    (msg) => {
      const phone = normalizePhoneForWA(empresa?.telefono);
      if (!phone) return;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [empresa?.telefono, normalizePhoneForWA]
  );

  // ✅ Convertir rangos bloqueados -> lista de días deshabilitados
  const buildDisabledDates = useCallback((ranges) => {
    const out = [];
    ranges.forEach((r) => {
      const d0 = startOfDay(r.desde);
      const d1 = startOfDay(r.hasta);
      let cur = d0;
      while (isBefore(cur, d1)) {
        out.push(cur);
        cur = addDays(cur, 1);
      }
    });
    return out;
  }, []);

  // ✅ Cargar bloqueos
  const cargarBloqueos = useCallback(async () => {
    if (!esAlquiler || !producto?.id) return;

    try {
      setLoadingBloqueos(true);

      const refBloq = collection(db, "productos", producto.id, "bloqueos");
      const snap = await getDocs(refBloq);

      const rows = snap.docs.map((d) => {
        const data = d.data();
        const desdeD = data.desde?.toDate ? data.desde.toDate() : new Date(data.desde);
        const hastaD = data.hasta?.toDate ? data.hasta.toDate() : new Date(data.hasta);

        return {
          id: d.id,
          desde: startOfDay(desdeD),
          hasta: startOfDay(hastaD),
          motivo: data.motivo || "Ocupado",
        };
      });

      setBloqueos(rows);
      setDisabledDates(buildDisabledDates(rows));
    } catch (e) {
      console.warn("Error leyendo bloqueos:", e);
      setBloqueos([]);
      setDisabledDates([]);
    } finally {
      setLoadingBloqueos(false);
    }
  }, [esAlquiler, producto?.id, buildDisabledDates]);

  // ✅ Próxima fecha libre
  const proximaDisponible = useMemo(() => {
    if (!esAlquiler) return "";
    const base = startOfDay(new Date());

    if (!disabledDates?.length) return base.toLocaleDateString("es-AR");

    for (let i = 0; i < 120; i++) {
      const d = addDays(base, i);
      const isDisabled = disabledDates.some((x) => isSameDay(x, d));
      if (!isDisabled) return d.toLocaleDateString("es-AR");
    }
    return "Consultar";
  }, [esAlquiler, disabledDates]);

  // ✅ Noches (checkout - checkin)
  const noches = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const n = differenceInCalendarDays(endDate, startDate);
    return n > 0 ? n : 0;
  }, [startDate, endDate]);

  const total = useMemo(() => {
    if (!noches) return 0;
    return noches * Number(huespedes || 0) * precioBase;
  }, [noches, huespedes, precioBase]);

  const rangoSolapa = (aDesde, aHasta, bDesde, bHasta) => aDesde < bHasta && aHasta > bDesde;

  const esRangoValido = useMemo(() => {
    if (!esAlquiler) return false;
    if (!startDate || !endDate) return false;
    if (!noches) return false;
    if (huespedes < minH || huespedes > maxH) return false;

    const haySolape = (bloqueos || []).some((b) =>
      rangoSolapa(startDate, endDate, b.desde, b.hasta)
    );

    return !haySolape;
  }, [esAlquiler, startDate, endDate, noches, huespedes, minH, maxH, bloqueos]);

  // ✅ Abrir modal
  const abrirModal = async () => {
    setImgActiva(galeria[0] || producto?.imagenUrl || imagenFallback || "");
    setShowModal(true);

    if (esAlquiler) {
      setHuespedes(minH);
      setStartDate(null);
      setEndDate(null);
      await cargarBloqueos();
    }
  };

  // ✅ + / - DIRECTO al carrito (y ahora el + pasa el producto completo con precio)
 const aumentar = () => {
  if (producto?.talle && !talleSeleccionado) {
    Swal.fire({
      icon: "warning",
      title: "Seleccioná una medida",
      toast: true,
      position: "top-end",
      timer: 1500,
      showConfirmButton: false,
    });
    return;
  }

  const talleKey = producto?.talle ? talleSeleccionado : "";

  incrementItem({
    ...producto,
    precio: precioMostrado,
    talle: talleKey || undefined,
  });

  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "success",
    title: `${producto?.nombre} agregado al carrito`,
    showConfirmButton: false,
    timer: 1200,
    timerProgressBar: true,
  });
};

 const disminuir = () => {
  if (!producto?.id) return;
  if (producto?.talle && !talleSeleccionado) return;
  if (qtyEnCarrito <= 0) return;

  const talleKey = producto?.talle ? talleSeleccionado : "";

  decrementItem({
    ...producto,
    precio: precioMostrado,
    talle: talleKey || undefined,
  });

  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "info",
    title: `${producto?.nombre} quitado del carrito`,
    showConfirmButton: false,
    timer: 900,
    timerProgressBar: true,
  });
};

  // ✅ Enviar consulta alquiler
  const enviarConsulta = () => {
    if (!esRangoValido) return;

    const msg = `Hola ${empresa?.nombre || ""} 👋
Quiero consultar disponibilidad para: ${producto?.nombre || ""}
📅 Desde: ${fmtAR(startDate)}
📅 Hasta: ${fmtAR(endDate)} (checkout)
👥 Huéspedes: ${huespedes}
🌙 Noches: ${noches}
💰 Total estimado: $${Number(total).toLocaleString("es-AR")}
¿Me confirmás si está disponible?`;

    openWA(msg);
    setShowModal(false);
  };

  return (
    <>
      <div
        className="card shadow-sm mb-0 d-flex flex-column"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          border: "none",
          minHeight: "300px",
          maxWidth: "320px",
        }}
      >
        {(producto?.imagenUrl || imagenFallback) && (
          <div
            className="position-relative"
            style={{
              width: "100%",
              height: "190px",
              overflow: "hidden",
              cursor: "pointer",
              flexShrink: 0,
              background: "#f5f5f5",
            }}
            onClick={abrirModal}
            title="Ver detalles"
          >
            <img
              src={producto?.imagenUrl || imagenFallback}
              alt={producto.nombre}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease, filter 0.3s ease",
                filter: "brightness(0.9)",
                display: "block",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.5)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            />
          </div>
        )}

        <div
          className="card-body d-flex flex-column text-center"
          style={{ width: "100%", padding: "12px", flexGrow: 1 }}
        >
          <h5
            className="card-title mb-1"
            style={{
              minHeight: "32px",
              fontWeight: 700,
              fontSize: "0.98rem",
              wordBreak: "break-word",
            }}
          >
            {producto?.nombre}
          </h5>

          <p className="card-text fw-bold fs-5 mb-2" style={{ minHeight: "26px" }}>
            ${Number(precioMostrado).toLocaleString("es-AR")}
            {textoReferenciaPrecio ? (
              <span
                className="fw-normal ms-2"
                style={{ fontSize: "0.72rem", opacity: 0.75, whiteSpace: "nowrap" }}
              >
                {textoReferenciaPrecio}
              </span>
            ) : null}
          </p>

          {esAlquiler && (
            <div
              className="mb-2"
              style={{
                fontSize: 13,
                background: "rgba(25,135,84,0.10)",
                border: "1px solid rgba(25,135,84,0.25)",
                borderRadius: 10,
                padding: "6px 6px",
              }}
            >
              <b>Próxima disponible:</b> {proximaDisponible} <br />
              <span style={{ opacity: 0.9 }}>
                Ocupación: {minH} a {maxH} huéspedes
              </span>
            </div>
          )}

          {!esAlquiler && producto?.talle && talles.length > 0 && (
            <div className="mb-2">
              <select
                className="form-select"
                value={talleSeleccionado}
                onChange={(e) => setTalleSeleccionado(e.target.value)}
              >
                <option value="">Seleccionar medida</option>
                {talles.map((row, i) => (
                  <option key={`${row.talle}-${i}`} value={row.talle}>
                    {row.talle}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mt-auto">
            {esAlquiler ? (
              <button className="btn btn-primary w-100" onClick={abrirModal} type="button">
                Consultar disponibilidad
              </button>
            ) : (
             <div className="d-flex align-items-center justify-content-between gap-2">
  {/* ✅ Cantidad centrada */}
  <div className="flex-grow-1 d-flex justify-content-center">
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ minWidth: 120 }}
    >
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={disminuir}
        disabled={qtyEnCarrito <= 0}
        type="button"
        style={{ width: 34, height: 34, padding: 0 }}
      >
        -
      </button>

      <span
        className="fw-bold text-center"
        style={{ width: 36, display: "inline-block" }}
      >
        {qtyEnCarrito}
      </span>

      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={aumentar}
        type="button"
        style={{ width: 34, height: 34, padding: 0 }}
      >
        +
      </button>
    </div>
  </div>

  {/* ✅ Botón bolsa (queda a la derecha)
  <button
    className="btn btn-success"
    onClick={aumentar}
    title="Agregar al carrito"
    type="button"
    style={{  
      width: 46,
      height: 38,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
      flexShrink: 0,
    }}
  >
   
  </button>  */}
</div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal fade show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.8)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 3000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-dialog"
            role="document"
            style={{ maxWidth: "760px", width: "94%", height: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-dark text-light h-100 overflow-auto rounded-4">
              <div className="modal-header border-0">
                <h5 className="modal-title">{producto?.nombre}</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body text-center">
                <img
                  src={imgActiva || producto?.imagenUrl || imagenFallback}
                  alt={producto?.nombre}
                  className="img-fluid mb-3"
                  style={{
                    width: "75%",
                    maxWidth: "420px",
                    height: "auto",
                    objectFit: "cover",
                    borderRadius: "10px",
                    margin: "0 auto",
                    display: "block",
                  }}
                />

                {galeria.length > 1 && (
                  <div
                    className="d-flex flex-wrap justify-content-center gap-2 mb-3"
                    style={{ userSelect: "none" }}
                  >
                    {galeria.map((url) => (
                      <button
                        key={url}
                        type="button"
                        className="p-0 border-0 bg-transparent"
                        onClick={() => setImgActiva(url)}
                        style={{
                          width: 70,
                          height: 70,
                          borderRadius: 10,
                          overflow: "hidden",
                          outline:
                            url === imgActiva
                              ? "3px solid #198754"
                              : "1px solid rgba(255,255,255,0.25)",
                        }}
                      >
                        <img
                          src={url}
                          alt="mini"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}

                {esAlquiler && (
                  <div
                    className="mt-3 text-start"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 16,
                      padding: 14,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div style={{ fontWeight: 900, fontSize: 16 }}>📅 Disponibilidad</div>
                      {loadingBloqueos ? (
                        <small className="text-warning">Cargando…</small>
                      ) : (
                        <small className="text-muted">Seleccioná un rango</small>
                      )}
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-bold mb-1">Cantidad de huéspedes</label>
                      <input
                        type="number"
                        className="form-control"
                        min={minH}
                        max={maxH}
                        value={huespedes}
                        onChange={(e) => setHuespedes(Number(e.target.value || 0))}
                      />
                      <div className="form-text text-light">Mín {minH} / Máx {maxH}</div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <DatePicker
                        inline
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(dates) => {
                          const [start, end] = dates || [];
                          setStartDate(start ? startOfDay(start) : null);
                          setEndDate(end ? startOfDay(end) : null);
                        }}
                        minDate={startOfDay(new Date())}
                        excludeDates={disabledDates}
                        dayClassName={(date) => {
                          const d = startOfDay(date);
                          const isDisabled = disabledDates.some((x) => isSameDay(x, d));
                          return isDisabled ? "nn-day-disabled" : "nn-day-available";
                        }}
                      />
                    </div>

                    <div className="mt-3" style={{ fontSize: 13, opacity: 0.95 }}>
                      <div><b>Desde:</b> {fmtAR(startDate)}</div>
                      <div><b>Hasta:</b> {fmtAR(endDate)} {endDate ? "(checkout)" : ""}</div>
                    </div>

                    <div
                      className="mt-3"
                      style={{
                        borderRadius: 14,
                        padding: 12,
                        background: "rgba(13,110,253,0.18)",
                      }}
                    >
                      <div><b>Noches:</b> {noches || "-"}</div>
                      <div><b>Precio base:</b> ${precioBase.toLocaleString("es-AR")}</div>
                      <div style={{ fontSize: 18, fontWeight: 900, marginTop: 6 }}>
                        Total estimado: ${Number(total).toLocaleString("es-AR")}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        *Estimado: noches × huéspedes × precio base
                      </div>
                    </div>

                    {!esRangoValido && (startDate || endDate) && (
                      <div className="alert alert-warning mt-3 mb-0">
                        El rango seleccionado no es válido o pisa días ocupados.
                      </div>
                    )}

                    <button
                      className="btn btn-success w-100 mt-3"
                      style={{ fontWeight: 900, borderRadius: 999 }}
                      disabled={!esRangoValido}
                      onClick={enviarConsulta}
                      type="button"
                    >
                      Enviar consulta por WhatsApp
                    </button>
                  </div>
                )}

                <h5 className="mt-1">{producto?.descripcion}</h5>
                <p className="mt-1">{producto?.detalle}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
